import {
  EMPTY,
  FilterInfoOperator,
  FormattingOperator,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import { QueryBuilderFilterType } from '@ardoq/query-builder';
import type { ComponentOrReferenceFormattingRule } from '@ardoq/data-model';
import { isArray, isFunction, isString } from 'lodash';
import {
  SelectOption,
  TypeNameTransformationResult,
  ValueTransformationResult,
} from './valueTransformations';
import {
  isTypeRule,
  type LegacyComponentOrReferenceFormattingRule,
  legacyTypeNamesToTypeNames,
} from '../../PerspectivesConditionalFormatting/normalizeRule';
import {
  DissectedRelativeDate,
  dissectRelativeDate,
  TimeFrameUnit,
} from '@ardoq/date-time';

export type OperatorTranslation = {
  newComparator: FormattingOperator;
  handleValueTransformation?: (
    rule: ComponentOrReferenceFormattingRule,
    options?: SelectOption[]
  ) => ValueTransformationResult;
  handleTypeNameTransformation?: (
    rule: LegacyComponentOrReferenceFormattingRule,
    options?: SelectOption[]
  ) => TypeNameTransformationResult;
  warning?: string;
};

type OperatorMappingKey = {
  oldComparator: FormattingOperator;
  isInverted: boolean;
  filterType: QueryBuilderFilterType;
  isEmptyValue: boolean;
};

type OperatorMapping =
  | OperatorTranslation
  | OperatorTranslation[]
  | ((
      rule: ComponentOrReferenceFormattingRule
    ) => OperatorTranslation | OperatorTranslation[] | null);

const isSupportedRelativeDate = (dissectedDate: DissectedRelativeDate) => {
  const isDayUnit = [
    TimeFrameUnit.DAY_AGO,
    TimeFrameUnit.DAY_FROM_NOW,
  ].includes(dissectedDate.unit);
  const isSupportedValue = [0, 1].includes(dissectedDate.value);

  return isDayUnit && isSupportedValue;
};

const skipMigrationForUnsupportedRelativeDate =
  (translation: OperatorTranslation | OperatorTranslation[]) =>
  (rule: ComponentOrReferenceFormattingRule) => {
    if (!isString(rule.value)) return translation;

    const dissectedDate = dissectRelativeDate(rule.value);

    if (!dissectedDate) return translation;

    return isSupportedRelativeDate(dissectedDate) ? translation : null;
  };

const normalizeRelativeDateValue: OperatorTranslation['handleValueTransformation'] =
  rule => {
    return {
      newValue: rule.value === 'now-0d' ? 'now+0d' : rule.value, // it's converting "0 days ago" to "Today"
      warnings: [],
    };
  };

/**
 * Maps old operator + isInverted + filterType combinations to new operators.
 * This is the single source of truth for operator migration.
 */
const createOperatorMapping = (): Map<string, OperatorMapping> => {
  const mappings = new Map<string, OperatorMapping>();

  const addMapping = (
    key: OperatorMappingKey,
    translation: OperatorMapping
  ) => {
    const mapKey = `${key.oldComparator}:${key.isInverted}:${key.filterType}:${key.isEmptyValue}`;
    if (mappings.has(mapKey)) {
      // eslint-disable-next-line no-console
      console.warn('There is a mapping duplication', mapKey);
    }
    mappings.set(mapKey, translation);
  };

  // NUMBER field mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: false,
      filterType: QueryBuilderFilterType.NUMBER,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.GREATER_THAN,
      isInverted: false,
      filterType: QueryBuilderFilterType.NUMBER,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.GREATER_THAN,
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.LESS_THAN,
      isInverted: false,
      filterType: QueryBuilderFilterType.NUMBER,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.LESS_THAN,
    }
  );

  // STRING field mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: false,
      filterType: QueryBuilderFilterType.STRING,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.CONTAINS,
      isInverted: false,
      filterType: QueryBuilderFilterType.STRING,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.CONTAINS,
    }
  );

  // SELECT field mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: false,
      filterType: QueryBuilderFilterType.SELECT,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
      handleValueTransformation: (oldRule, selectOptions) => {
        const { value, color } = oldRule;
        if (!selectOptions)
          return {
            warnings: ['no options, transformation is skipped'],
            newValue: value,
          };

        if (!isString(value)) {
          return {
            warnings: [`value is not string. Value: ${value}`],
            newValue: value,
          };
        }

        return {
          newValue: {
            // Map each option to a ColorFormatting object
            ...Object.fromEntries(
              selectOptions.map(({ value: optionValue }) => {
                const isSelected = optionValue === value;

                return [
                  optionValue,
                  {
                    color: isSelected ? color : '', // Use rule color for selected value
                    isDisabled: !isSelected,
                  },
                ];
              })
            ),
            // Always include "No value" option
            [EMPTY]: {
              color: '',
              isDisabled: true,
            },
          },
          warnings: [],
        };
      },
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.CONTAINS,
      isInverted: false,
      filterType: QueryBuilderFilterType.SELECT,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
      handleValueTransformation: (oldRule, selectOptions) => {
        const { value, color } = oldRule;
        if (!selectOptions)
          return {
            warnings: ['no options, transformation is skipped'],
            newValue: value,
          };

        if (!isString(value)) {
          return {
            warnings: [`value is not string. Value: ${value}`],
            newValue: value,
          };
        }

        return {
          newValue: {
            // Map each option to a ColorFormatting object
            ...Object.fromEntries(
              selectOptions.map(({ value: optionValue }) => {
                const isSelected = optionValue
                  .toLowerCase()
                  .includes(value.toLowerCase());

                return [
                  optionValue,
                  {
                    color: isSelected ? color : '', // Use rule color for selected value
                    isDisabled: !isSelected,
                  },
                ];
              })
            ),
            // Always include "No value" option
            [EMPTY]: {
              color: '',
              isDisabled: true,
            },
          },
          warnings: [],
        };
      },
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: true,
      filterType: QueryBuilderFilterType.SELECT,
      isEmptyValue: true,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
      handleValueTransformation: (oldRule, selectOptions) => {
        const { value, color } = oldRule;
        if (!selectOptions)
          return {
            warnings: ['no options, transformation is skipped'],
            newValue: value,
          };

        return {
          newValue: {
            // Map each option to a ColorFormatting object
            ...Object.fromEntries(
              selectOptions.map(({ value: optionValue }) => {
                return [
                  optionValue,
                  {
                    color: color, // Use rule color for selected value
                    isDisabled: false,
                  },
                ];
              })
            ),
            // Always include "No value" option
            [EMPTY]: {
              color: '',
              isDisabled: true,
            },
          },
          warnings: [],
        };
      },
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.ID_MATCH,
      isInverted: false,
      filterType: QueryBuilderFilterType.SELECT,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.ID_MATCH,
      handleTypeNameTransformation: (oldRule, options) => {
        // TODO(typeName-migration): Replace with oldRule.typeNames once BE has migrated all v0 rules.
        const typeNames = legacyTypeNamesToTypeNames(oldRule);
        const { value } = oldRule;
        if (!isTypeRule(oldRule as ComponentOrReferenceFormattingRule)) {
          return {
            newTypeNames: typeNames,
            warnings: ['expect to be type rule'],
          };
        }
        if (!options) {
          return {
            newTypeNames: typeNames,
            warnings: ['no options, transformation is skipped'],
          };
        }
        if (!isString(value) || !options.find(({ value: v }) => v === value)) {
          return {
            newTypeNames: typeNames,
            warnings: ['value is not componentTypes or referenceTypes'],
          };
        }
        return { newTypeNames: [value], warnings: [] };
      },
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.ID_MATCH,
      isInverted: true,
      filterType: QueryBuilderFilterType.SELECT,
      isEmptyValue: false,
    },
    {
      newComparator: FormattingRuleOperator.ID_DOES_NOT_MATCH,
      handleTypeNameTransformation: (oldRule, options) => {
        // TODO(typeName-migration): Replace with oldRule.typeNames once BE has migrated all v0 rules.
        const typeNames = legacyTypeNamesToTypeNames(oldRule);
        const { value } = oldRule;
        if (!isTypeRule(oldRule as ComponentOrReferenceFormattingRule)) {
          return {
            newTypeNames: typeNames,
            warnings: ['expect to be type rule'],
          };
        }
        if (!options) {
          return {
            newTypeNames: typeNames,
            warnings: ['no options, transformation is skipped'],
          };
        }
        if (!isString(value) || !options.find(({ value: v }) => v === value)) {
          return {
            newTypeNames: typeNames,
            warnings: ['value is not componentTypes or referenceTypes'],
          };
        }
        return { newTypeNames: [value], warnings: [] };
      },
    }
  );

  // SELECT_MULTIPLE mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: false,
      filterType: QueryBuilderFilterType.SELECT_MULTIPLE,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.CONTAINS,
      isInverted: false,
      filterType: QueryBuilderFilterType.SELECT_MULTIPLE,
      isEmptyValue: false,
    },
    {
      newComparator: FormattingRuleOperator.LIST_CONTAINS,
    }
  );

  // ASYNC_COMPONENT_NAME mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: false,
      filterType: QueryBuilderFilterType.ASYNC_COMPONENT_NAME,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: true,
      filterType: QueryBuilderFilterType.ASYNC_COMPONENT_NAME,
      isEmptyValue: false,
    },
    {
      newComparator: FormattingRuleOperator.NOT_EQUAL,
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.CONTAINS,
      isInverted: false,
      filterType: QueryBuilderFilterType.ASYNC_COMPONENT_NAME,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.CONTAINS,
    }
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.CONTAINS,
      isInverted: true,
      filterType: QueryBuilderFilterType.ASYNC_COMPONENT_NAME,
      isEmptyValue: false,
    },
    {
      newComparator: FormattingRuleOperator.NOT_CONTAINS,
    }
  );

  // ID matcher fields (OID, PARENT, SOURCE, TARGET, ROOT_WORKSPACE)
  const idMatcherTypes = [
    QueryBuilderFilterType.ASYNC_COMPONENT_OID,
    QueryBuilderFilterType.ASYNC_REFERENCE_OID,
    QueryBuilderFilterType.ASYNC_PARENT,
    QueryBuilderFilterType.ASYNC_SOURCE,
    QueryBuilderFilterType.ASYNC_TARGET,
    QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE,
  ];

  idMatcherTypes.forEach(filterType => {
    addMapping(
      {
        oldComparator: FilterInfoOperator.ID_MATCH,
        isInverted: false,
        filterType,
        isEmptyValue: false,
      },
      {
        newComparator: FilterInfoOperator.ID_MATCH,
      }
    );

    addMapping(
      {
        oldComparator: FilterInfoOperator.ID_MATCH,
        isInverted: true,
        filterType,
        isEmptyValue: false,
      },
      {
        newComparator: FormattingRuleOperator.ID_DOES_NOT_MATCH,
      }
    );
  });

  // ASYNC_USER mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.EQUALS,
      isInverted: false,
      filterType: QueryBuilderFilterType.ASYNC_USER,
      isEmptyValue: false,
    },
    {
      newComparator: FilterInfoOperator.EQUALS,
    }
  );

  // DATE mappings

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_MATCH,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_MATCH,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_EARLIER,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_EARLIER,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_LATER,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_LATER,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  // DATE_TIME mappings

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_MATCH,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE_TIME,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_MATCH,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_EARLIER,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE_TIME,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_EARLIER,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_LATER,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE_TIME,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_LATER,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  // DATE_RANGE and DATE_TIME_RANGE mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_RANGE_CONTAINS,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE_RANGE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_RANGE_CONTAINS,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE_RANGE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN,
      isInverted: true,
      filterType: QueryBuilderFilterType.DATE_RANGE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_RANGE_CONTAINS,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_RANGE_BEFORE,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE_RANGE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_RANGE_BEFORE,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  addMapping(
    {
      oldComparator: FilterInfoOperator.DATE_RANGE_AFTER,
      isInverted: false,
      filterType: QueryBuilderFilterType.DATE_RANGE,
      isEmptyValue: false,
    },
    skipMigrationForUnsupportedRelativeDate({
      newComparator: FilterInfoOperator.DATE_RANGE_AFTER,
      handleValueTransformation: normalizeRelativeDateValue,
    })
  );

  // BOOLEAN mappings
  addMapping(
    {
      oldComparator: FilterInfoOperator.BOOLEAN,
      isInverted: false,
      filterType: QueryBuilderFilterType.BOOLEAN,
      isEmptyValue: false,
    },
    rule => {
      if (rule.value === true) {
        return {
          newComparator: FilterInfoOperator.BOOLEAN,
          warning: `Boolean field migrated: checked`,
        };
      }

      // value is false
      return [
        {
          newComparator: FormattingRuleOperator.NOT_BOOLEAN,
          warning: `Boolean field migrated: unchecked`,
        },
        {
          newComparator: FormattingRuleOperator.IS_EMPTY,
          warning: `Boolean field migrated: unchecked`,
        },
      ];
    }
  );

  return mappings;
};

const operatorMappings = createOperatorMapping();

export const getOperatorTranslations = (
  { oldComparator, isInverted, filterType, isEmptyValue }: OperatorMappingKey,
  rule: ComponentOrReferenceFormattingRule
): OperatorTranslation[] | null => {
  // '' or '  ' in old rule is act the same as 'some value'. But in new conditional formatting we don't allow empty value
  // null or '' or ' ' and remove them during applying. That's why we need skip translating for '' or '  '
  if (isString(rule.value) && rule.value.trim() === '') {
    return null;
  }

  const key = [oldComparator, isInverted, filterType, isEmptyValue].join(':');

  const operatorMapping = operatorMappings.get(key) || null;

  if (!operatorMapping) return null;

  if (!isFunction(operatorMapping)) {
    return isArray(operatorMapping) ? operatorMapping : [operatorMapping];
  }

  const mapping = operatorMapping(rule);

  if (!mapping) return null;

  return isArray(mapping) ? mapping : [mapping];
};
